import { i18n } from "@lingui/core";
import { captureException } from "@sentry/react-native";
import { setDefaultOptions } from "date-fns";
import { enUS, fr } from "date-fns/locale";
import { Provider, useAtomValue, useSetAtom, useStore } from "jotai";
import {
  useCallback,
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Platform } from "react-native";

import { SupportedLanguage } from "@whoosh/design-system/src/components/controls";

import { LocaleTransitionOverlay } from "./LocaleTransitionOverlay";
import {
  effectiveLanguageAtom,
  hydrateStoredLanguagePreferenceAtom,
  storedLanguagePreferenceHydratedAtom,
} from "./atoms";

const AppliedLanguageContext = createContext<SupportedLanguage>("en");
const LanguagePreferenceStoreContext = createContext<ReturnType<
  typeof useStore
> | null>(null);
type LocaleTransitionAnimationActivityReporter = (
  id: symbol,
  suppressed: boolean
) => void;
const LocaleTransitionAnimationContext =
  createContext<LocaleTransitionAnimationActivityReporter | null>(null);

type LocaleBoundaryLoaderActivity = {
  active: boolean;
  count: number;
};

const LocaleBoundaryLoaderActivityContext = createContext<
  ((id: symbol, activity: LocaleBoundaryLoaderActivity | null) => void) | null
>(null);

export const useAppliedLanguage = () => useContext(AppliedLanguageContext);
export const useLanguagePreferenceStore = () =>
  useContext(LanguagePreferenceStoreContext);
export const useReportLocaleBoundaryLoaderActivity = () =>
  useContext(LocaleBoundaryLoaderActivityContext);
export const useSuppressLocaleTransitionAnimation = () => {
  const reportAnimationActivity = useContext(LocaleTransitionAnimationContext);
  const sourceId = useRef(Symbol("locale-transition-animation-source")).current;

  useLayoutEffect(() => {
    reportAnimationActivity?.(sourceId, true);

    return () => reportAnimationActivity?.(sourceId, false);
  }, [reportAnimationActivity, sourceId]);
};

type LanguagePreferenceProviderProps = {
  // Recreate the element tree after locale activation so imperative translations
  // re-evaluate while React preserves the mounted component instances.
  children: (appliedLanguage: SupportedLanguage) => ReactNode;
  onReady?: () => void;
};

export const LanguagePreferenceProvider = ({
  children,
  onReady,
}: LanguagePreferenceProviderProps) => (
  <Provider>
    <LanguagePreferenceController onReady={onReady}>
      {children}
    </LanguagePreferenceController>
  </Provider>
);

const LanguagePreferenceController = ({
  children,
  onReady,
}: LanguagePreferenceProviderProps) => {
  const languagePreferenceStore = useStore();
  const language = useAtomValue(effectiveLanguageAtom);
  const [appliedLanguage, setAppliedLanguage] = useState(language);
  const [pendingLanguage, setPendingLanguage] =
    useState<SupportedLanguage | null>(null);
  const [
    localeTransitionAnimationSuppressed,
    setLocaleTransitionAnimationSuppressed,
  ] = useState(false);
  const localeTransitionAnimationSourcesRef = useRef(new Set<symbol>());
  const [activeLoaderCount, setActiveLoaderCount] = useState(0);
  const boundaryLoaderActivityRef = useRef(
    new Map<symbol, LocaleBoundaryLoaderActivity>()
  );
  const [hasRenderedChildren, setHasRenderedChildren] = useState(false);
  const storedLanguagePreferenceHydrated = useAtomValue(
    storedLanguagePreferenceHydratedAtom
  );
  const hydrateStoredLanguagePreference = useSetAtom(
    hydrateStoredLanguagePreferenceAtom
  );

  useEffect(() => {
    void hydrateStoredLanguagePreference().catch((error: unknown) => {
      captureException(error);
    });
  }, [hydrateStoredLanguagePreference]);

  const applyLanguage = useCallback((nextLanguage: SupportedLanguage) => {
    setDefaultOptions({ locale: nextLanguage === "fr" ? fr : enUS });

    if (i18n.locale !== nextLanguage) {
      i18n.activate(nextLanguage);
    }

    if (Platform.OS === "web") {
      document.documentElement.lang = nextLanguage;
    }

    setAppliedLanguage(nextLanguage);
  }, []);

  useLayoutEffect(() => {
    if (!storedLanguagePreferenceHydrated) {
      return;
    }

    if (!hasRenderedChildren) {
      applyLanguage(language);
      setHasRenderedChildren(true);
      return;
    }

    if (language !== appliedLanguage) {
      if (localeTransitionAnimationSuppressed) {
        setPendingLanguage(null);
        applyLanguage(language);
        return;
      }

      setPendingLanguage(language);
      return;
    }

    // The desired locale can roll back before the fade-in completes, for
    // example when persisting an authenticated preference fails.
    setPendingLanguage((currentLanguage) =>
      currentLanguage && currentLanguage !== appliedLanguage
        ? null
        : currentLanguage
    );
  }, [
    appliedLanguage,
    applyLanguage,
    hasRenderedChildren,
    language,
    localeTransitionAnimationSuppressed,
    storedLanguagePreferenceHydrated,
  ]);

  const reportLocaleTransitionAnimationActivity = useCallback(
    (id: symbol, suppressed: boolean) => {
      if (suppressed) {
        localeTransitionAnimationSourcesRef.current.add(id);
      } else {
        localeTransitionAnimationSourcesRef.current.delete(id);
      }

      setLocaleTransitionAnimationSuppressed(
        localeTransitionAnimationSourcesRef.current.size > 0
      );
    },
    []
  );

  const applyPendingLanguage = useCallback(
    (nextLanguage: SupportedLanguage) => {
      if (pendingLanguage === nextLanguage) {
        applyLanguage(nextLanguage);
      }
    },
    [applyLanguage, pendingLanguage]
  );

  const finishLanguageTransition = useCallback(
    (finishedLanguage: SupportedLanguage) => {
      setPendingLanguage((currentLanguage) =>
        currentLanguage === finishedLanguage ? null : currentLanguage
      );
    },
    []
  );

  const reportBoundaryLoaderActivity = useCallback(
    (id: symbol, activity: LocaleBoundaryLoaderActivity | null) => {
      if (activity) {
        boundaryLoaderActivityRef.current.set(id, activity);
      } else {
        boundaryLoaderActivityRef.current.delete(id);
      }

      const activeCount = Array.from(
        boundaryLoaderActivityRef.current.values()
      ).reduce(
        (count, boundaryActivity) =>
          boundaryActivity.active ? count + boundaryActivity.count : count,
        0
      );

      setActiveLoaderCount(activeCount);
    },
    []
  );

  useEffect(() => {
    if (hasRenderedChildren) {
      onReady?.();
    }
  }, [hasRenderedChildren, onReady]);

  return (
    <LanguagePreferenceStoreContext.Provider value={languagePreferenceStore}>
      <AppliedLanguageContext.Provider value={appliedLanguage}>
        <LocaleTransitionAnimationContext.Provider
          value={reportLocaleTransitionAnimationActivity}
        >
          <LocaleBoundaryLoaderActivityContext.Provider
            value={reportBoundaryLoaderActivity}
          >
            {hasRenderedChildren ? children(appliedLanguage) : null}
          </LocaleBoundaryLoaderActivityContext.Provider>
        </LocaleTransitionAnimationContext.Provider>
        <LocaleTransitionOverlay
          appliedLanguage={appliedLanguage}
          contentIsLoading={activeLoaderCount > 0}
          pendingLanguage={pendingLanguage}
          onCovered={applyPendingLanguage}
          onFinished={finishLanguageTransition}
        />
      </AppliedLanguageContext.Provider>
    </LanguagePreferenceStoreContext.Provider>
  );
};
